import React, { useEffect, useState, useRef } from "react";
import {
  collection,
  query,
  where,
  onSnapshot,
  addDoc,
  deleteDoc,
  doc,
  updateDoc,
} from "firebase/firestore";
import { db, auth } from "../firebase";
import { useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";

function MainScreen() {
  const [committees, setCommittees] = useState([]);
  const [newCommittee, setNewCommittee] = useState("");
  const [agendaInputs, setAgendaInputs] = useState({});
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();

  // Close dropdown if clicked outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!auth.currentUser) return;

    const committeesRef = collection(db, "committees");

    const q = query(committeesRef, where("createdBy", "==", auth.currentUser.uid));
    const unsubscribeCreator = onSnapshot(q, (snapshot) => {
      const owned = snapshot.docs.map((doc) => {
        const c = { id: doc.id, ...doc.data(), _source: "owned" };
        c.permissions = { view: true, write: true };
        return c;
      });
      setCommittees((prev) => {
        const shared = prev.filter((c) => c._source === "shared");
        return [...owned, ...shared];
      });
    });

    const unsubscribeAll = onSnapshot(committeesRef, (snapshot) => {
      const userEmail = auth.currentUser.email;
      const shared = snapshot.docs
        .map((doc) => {
          const c = { id: doc.id, ...doc.data(), _source: "shared" };
          const entry = (c.accessList || []).find((u) => u.email === userEmail);
          c.permissions = entry?.permissions || { view: false, write: false };
          return c;
        })
        .filter((c) => c.createdBy !== auth.currentUser.uid && c.permissions.view);
      setCommittees((prev) => {
        const owned = prev.filter((c) => c._source === "owned");
        return [...owned, ...shared];
      });
    });

    return () => {
      unsubscribeCreator();
      unsubscribeAll();
    };
  }, []);

  const handleAddCommittee = async (e) => {
    e.preventDefault();
    if (!newCommittee.trim()) return;
    await addDoc(collection(db, "committees"), {
      name: newCommittee,
      createdBy: auth.currentUser.uid,
      createdAt: new Date(),
      globalAgenda: "",
      accessList: [],
    });
    setNewCommittee("");
  };

  const handleDelete = async (id, createdBy) => {
    if (createdBy !== auth.currentUser.uid) {
      alert("You do not have permission to delete this committee.");
      return;
    }
    const confirmed = window.confirm("Are you sure you want to delete this committee?");
    if (!confirmed) return;
    await deleteDoc(doc(db, "committees", id));
  };

  const handleAgendaSave = async (id) => {
    if (!agendaInputs[id]?.trim()) return;
    const committee = committees.find((c) => c.id === id);
    if (!committee || !committee.permissions.write) {
      alert("You do not have permission to edit the agenda.");
      return;
    }
    await updateDoc(doc(db, "committees", id), {
      globalAgenda: agendaInputs[id],
    });
    setAgendaInputs((prev) => ({ ...prev, [id]: "" }));
  };

  const handleLogout = async () => {
    await signOut(auth);
    navigate("/");
  };

  const user = auth.currentUser;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100">My Committees</h1>

        {/* Profile Dropdown */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setShowProfileMenu((prev) => !prev)}
            className="flex items-center gap-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 px-3 py-2 rounded-full shadow hover:bg-gray-100 dark:hover:bg-gray-700 transition"
          >
            <img
              src={user?.photoURL || "https://www.svgrepo.com/show/452030/avatar-default.svg"}
              alt="User"
              className="w-8 h-8 rounded-full object-cover"
            />
            <svg
              className={`w-4 h-4 text-gray-600 dark:text-gray-300 transform transition-transform ${
                showProfileMenu ? "rotate-180" : ""
              }`}
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {/* Dropdown Menu */}
          {showProfileMenu && (
            <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg py-2 z-50">
              <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700">
                <p className="text-sm text-gray-700 dark:text-gray-200 font-medium">
                  {user?.displayName || "User"}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{user?.email}</p>
              </div>
              <button
                onClick={handleLogout}
                className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/40 transition"
              >
                Logout
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Add Committee */}
      <form onSubmit={handleAddCommittee} className="flex gap-2 mb-6">
        <input
          type="text"
          placeholder="Enter committee name"
          className="flex-grow px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700"
          value={newCommittee}
          onChange={(e) => setNewCommittee(e.target.value)}
        />
        <button
          type="submit"
          className="bg-blue-600 dark:bg-blue-700 text-white px-4 py-2 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-800 transition"
        >
          Add
        </button>
      </form>

      {/* Committees List */}
      {committees.length === 0 ? (
        <p className="text-gray-500 dark:text-gray-300">
          No committees yet. Create one above or check access granted to you.
        </p>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {committees.map((c) => (
            <div
              key={c.id}
              className="bg-white dark:bg-gray-800 shadow-md rounded-xl p-6 border hover:shadow-lg transition relative dark:border-gray-700"
            >
              <div onClick={() => navigate(`/committee/${c.id}`)} className="cursor-pointer">
                <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-100 mb-2">{c.name}</h2>
                <p className="text-sm text-gray-400 dark:text-gray-400">
                  Created {c.createdAt?.toDate?.().toLocaleString?.() ?? "recently"}
                </p>
                {c._source === "shared" && (
                  <p className="text-xs text-blue-500 dark:text-blue-300 italic">Shared with you</p>
                )}
              </div>

              {/* Global Agenda */}
              <div className="mt-4">
                <h3 className="text-md font-medium text-gray-700 dark:text-gray-100 mb-1">Global Agenda</h3>
                {c.globalAgenda ? (
                  <p className="text-gray-600 dark:text-gray-300 mb-2">
                    Current: <span className="font-semibold">{c.globalAgenda}</span>
                  </p>
                ) : (
                  <p className="text-gray-400 italic mb-2">No agenda set</p>
                )}

                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Set new agenda"
                    className="flex-grow px-3 py-1 border rounded-lg focus:ring-2 focus:ring-blue-400 outline-none dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600"
                    value={agendaInputs[c.id] || ""}
                    onChange={(e) =>
                      setAgendaInputs((prev) => ({
                        ...prev,
                        [c.id]: e.target.value,
                      }))
                    }
                    disabled={!c.permissions.write}
                  />
                  <button
                    onClick={() => handleAgendaSave(c.id)}
                    className={`px-3 py-1 rounded-lg ${
                      c.permissions.write
                        ? "bg-green-600 dark:bg-green-700 text-white hover:bg-green-700 dark:hover:bg-green-800"
                        : "bg-gray-400 text-gray-200 cursor-not-allowed"
                    }`}
                    disabled={!c.permissions.write}
                  >
                    Save
                  </button>
                </div>
              </div>

              {c.createdBy === auth.currentUser.uid && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(c.id, c.createdBy);
                  }}
                  className="absolute top-3 right-3 text-red-500 hover:text-red-700"
                >
                  ✕
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default MainScreen;
